import { useState, useEffect } from "react";
import { collection, getDocs, query, where, doc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { X, Clock, Trash2 } from "lucide-react";
import { toast } from "react-hot-toast";

const ViewCaseEventsModal = ({ isOpen, onClose, caseData, onAddEventClick }) => {
  const { globalUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState(null);

  useEffect(() => {
    if (isOpen && caseData) {
      fetchCaseEvents();
    }
  }, [isOpen, caseData]);

  const fetchCaseEvents = async () => {
    if (!caseData?.caseNumber) return;

    setLoading(true);
    try {
      const eventsQuery = query(
        collection(db, "lawyers", globalUser.uid, "events"),
        where("Case No", "==", caseData.caseNumber)
      );
      const eventsSnapshot = await getDocs(eventsQuery);
      const eventsData = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by date and time
      eventsData.sort((a, b) => (b.time1 || 0) - (a.time1 || 0));
      setEvents(eventsData);
    } catch (error) {
      console.error("Error fetching case events:", error);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    setDeletingEventId(eventId);
    try {
      const eventDocRef = doc(db, "lawyers", globalUser.uid, "events", eventId);
      await deleteDoc(eventDocRef);

      // Remove from local state
      setEvents(events.filter(event => event.id !== eventId));
      toast.success("Event deleted successfully!");
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Failed to delete event");
    } finally {
      setDeletingEventId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1A1535] rounded-xl border border-gray-700 max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Clock className="text-yellow-500" size={20} />
            <h3 className="text-lg font-semibold text-white">
              Events for Case: {caseData?.caseNumber}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Events List */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-8 text-gray-300">Loading events...</div>
          ) : events.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No events found for this case.
            </div>
          ) : (
            <div className="space-y-3">
              {events.map((event) => (
                <div key={event.id} className="flex items-center gap-3">
                  <div className="bg-[#0A061E] border border-gray-700 rounded-lg p-3 flex-1">
                    <div className="flex-1">
                      <h4 className="text-white font-semibold text-base">{event.title}</h4>
                      {event.description && (
                        <p className="text-gray-400 text-sm mt-1">{event.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs">
                        <span className="text-gray-500">
                          Date: <span className="text-gray-300">{event.date}</span>
                        </span>
                        <span className="text-gray-500">
                          Time: <span className="text-gray-300">{event.time}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteEvent(event.id)}
                    disabled={deletingEventId === event.id}
                    className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    title="Delete event"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with Add Event Button */}
        <div className="p-5 border-t border-gray-700 flex justify-center">
          <button
            onClick={onAddEventClick}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 px-6 rounded-lg transition-colors text-sm"
          >
            Add Event
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewCaseEventsModal;
